/* eslint-disable consistent-return */
/* eslint-disable no-nested-ternary */
const _ = require('underscore');
const diff = require('deep-diff').diff;
const delay = (ms) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

const cachedNamespace = {
  autodesk: true,
};

export class ForgeSchemaAPIHelper {
  /**
   * Instantiates a Forge Schema Api Handler
   * @param {string} host The address of the forge api server to connect to.
   * @param {string} oathToken, The oath token to authenticate yourself to forge api.
   * @param {object} request An object that supports making rest requests.
   * @param {object} tokenGenerator An object that can refresh the token.
   * @constructor
   */
  constructor(host, oathToken, request) {
    this._host = host;
    this._oathToken = oathToken;
    this._request = request;
    this._log = [];
  }

  log = (line) => {
    this._log.push(line);
    // console.log(line);
  };

  logInfo = (line) => {
    this._log.push(line);
    // console.info(line);
  };

  logWarn = (line) => {
    this._log.push(line);
    console.warn(line);
  };

  logError = (line) => {
    this._log.push(line);
    console.error(line);
  };

  /**
   * creates a schema in forge schema server
   * @param {string} schema Json data of a valid HFDM schema
   * @param {string} extensionId The extensionId of the extension that the schema
   * will be created under. (entityId:extensionName)
   * @param {string} extensionStatus What the extensionStatus os the schema will be (draft or published)
   * @param {string} version optional specified version.
   * @return {*} Returns a promise containing the results of the creation
   */
  createSchema = (schema, extensionId, extensionStatus, version) => {
    const payload = {
      data: {
        type: 'versions',
        attributes: {
          extensionId,
          extensionStatus,
          schema,
        },
      },
    };
    if (version) {
      payload.data.attributes['version'] = version;
    }
    const options = {
      url: this._host + '/extensions/' + extensionId + '/versions',
      method: 'POST',
      headers: {
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
        'x-ads-fcs': true,
      },
      auth: {
        bearer: this._oathToken,
      },
      body: JSON.stringify(payload),
    };
    return this._request(
      this._oathToken,
      this._host,
      '/extensions/' + extensionId + '/versions',
      options
    );
  };

  /**
   * Uploads a schema to forge schema server
   * @param {string} schema Json data of a valid hdfm schema.
   * @param {boolean} retried If uploading failed and was retried.
   * @return {Promise} Returns a promise containing the results of the creation
   */
  uploadSchema = async (schema, retried) => {
    const schemaName = schema.typeid.split(':')[1];
    const namespaceName = schema.typeid.split(':')[0];
    // 1. check if the namespace is exist.
    let namespace;
    try {
      namespace = await this.getNamespace(namespaceName);
    } catch (err) {
      if (err.code === '1038') {
        // Error when fetching namespace = namespace not exist.
        this.log(`The namespace ${namespaceName} doesn't exist.`);
      }
    }

    if (namespace === undefined) {
      await this.createNamespaces(namespaceName);
    }

    const extensionName = schemaName.split('-')[0];
    const schemaVersion = schemaName.split('-')[1];
    const extensionId = 'propertySets:' + namespaceName + ':' + extensionName;

    let response;
    try {
      response = await this.getTip(extensionId);
    } catch (err) {
      this.log('The tip version of ' + extensionName + " doesn't exist.");
    }
    if (response !== undefined) {
      const tipSchemaId = response.attributes.schema.typeid;
      if (tipSchemaId === schema.typeid) {
        // case 1 - The tip version is equal to this version.
        this.log(extensionName + ' already published');
        return;
      } else {
        const tipVersion = tipSchemaId.split('-')[1];
        if (tipVersion > schemaVersion) {
          // is safe to do string compare?
          // case 2 - The tip version is higher than this version.
          this.log(
            `${extensionName} with version - ${tipVersion} - already published, skip this version - ${schemaVersion}.`
          );
          return;
        }
        // check the versions.
        // case 3 - Upgrade this schema.
        this.log(
          `${extensionName}-${tipVersion} already exist and need upgrade to latest version - ${schemaVersion}.`
        );
        this.logInfo('Upgrading schema: ' + schemaName);
        return this.upgradeExistSchema(schema, retried);
      }
    } else {
      // check the extension.
      try {
        response = await this.getExtension(extensionId);
      } catch (err) {
        this.log('The extension - ' + extensionName + " - doesn't exist");
      }

      // case 4 - Publish new schema.
      if (response === undefined) {
        this.logInfo('Publishing schema: ' + schemaName);
        return this.createNewSchema(schema, retried);
      } else {
        // case 5 - Fix the schema with extension but not publish.
        this.logInfo('Fix schema: ' + schemaName);
        return this.fixSchema(schema, retried);
      }
    }
  };

  fixSchema = async (schema, retried) => {
    const schemaName = schema.typeid.split(':')[1];
    const namespaceName = schema.typeid.split(':')[0];
    const extensionName = schemaName.split('-')[0];
    const schemaVersion = schemaName.split('-')[1];
    const extensionId = 'propertySets:' + namespaceName + ':' + extensionName;
    return this.createSchema(schema, extensionId, 'draft', schemaVersion)
      .then(() => {
        this.logInfo(' Completed upload of: ' + schemaName);
        return delay(1000);
      })
      .then(() => {
        this.logInfo('Draft schema: ' + schemaName);
        return this.getDraft('propertySets:' + schema.typeid.split('-')[0]);
      })
      .then((result) => {
        const changes = diff(schema, result.attributes.schema);
        this.logInfo('Published schema: ' + schemaName);
        if (changes) {
          this.log('Changes:');
          this.log(JSON.stringify(changes));
        }
        return this.createSchema(schema, extensionId, 'published', schemaVersion);
      })
      .catch((err) => {
        this.logWarn(extensionName + ' failed to upload');
        this.logWarn(err.error);
        if (!retried) {
          return this.uploadSchema(schema, true);
        } else {
          this.logWarn(
            'Failed with retry, please manually check the result and fix it and try again.'
          );
        }
      });
  };

  createNewSchema = async (schema, retried) => {
    const schemaName = schema.typeid.split(':')[1];
    const namespaceName = schema.typeid.split(':')[0];
    const extensionName = schemaName.split('-')[0];
    const schemaVersion = schemaName.split('-')[1];
    const extensionId = 'propertySets:' + namespaceName + ':' + extensionName;
    return this.createExtension('propertySets:' + namespaceName, extensionName)
      .catch((err) => {
        this.log(err.message);
      })
      .then(() => {
        return this.createSchema(schema, extensionId, 'draft', schemaVersion);
      })
      .then(() => {
        this.logInfo(' Completed upload of: ' + schemaName);
        return delay(1000);
      })
      .then(() => {
        this.logInfo('Draft schema: ' + schemaName);
        return this.getDraft('propertySets:' + schema.typeid.split('-')[0]);
      })
      .then((result) => {
        const changes = diff(schema, result.attributes.schema);
        this.logInfo('Published schema: ' + schemaName);
        if (changes) {
          this.log('Changes:');
          this.log(JSON.stringify(changes));
        }
        return this.createSchema(schema, extensionId, 'published', schemaVersion);
      })
      .catch((err) => {
        this.logWarn(extensionName + ' failed to upload');
        this.logWarn(err.error);
        if (!retried) {
          return this.uploadSchema(schema, true);
        } else {
          this.logWarn(
            'Failed with retry, please manually check the result and fix it and try again.'
          );
        }
      });
  };

  upgradeExistSchema = async (schema, retried) => {
    const schemaName = schema.typeid.split(':')[1];
    const namespaceName = schema.typeid.split(':')[0];
    const extensionName = schemaName.split('-')[0];
    const schemaVersion = schemaName.split('-')[1];
    const extensionId = 'propertySets:' + namespaceName + ':' + extensionName;

    return this.getDraft(extensionId)
      .then((result) => {
        const changes = diff(schema, result.attributes.schema);
        if (!changes) {
          // To fix the data corruption, we have to publish a no-change version.
          this.logWarn('No change, why a higher version exist.');
        }
        this.log('Changes aginst draft version:');
        this.log(JSON.stringify(changes));
        this.logInfo('Published schema: ' + schemaName);
        return this.createSchema(schema, extensionId, 'published', schemaVersion)
          .then(() => {
            this.logInfo(' Completed upload of: ' + schemaName);
            return delay(1000);
          })
          .catch((err) => {
            this.logWarn(extensionName + ' failed to upload');
            this.logWarn(err.error);
          });
      })
      .catch((err) => {
        this.logWarn(extensionName + ' failed to upload');
        this.logWarn(err.error);
        if (!retried) {
          return this.uploadSchema(schema, true);
        }
      });
  };

  /**
   * Orders schemas based on dependicies then uploads schema to forge schema server
   * @param {Array.<object>} schemas Json data of a valid hdfm schema.
   */
  bulkUploadSchema = async (schemas) => {
    this.log('Bulk uploading starting');
    const sortedSchemas = this.sortSchemas(schemas);
    for (let i = 0; i < sortedSchemas.length; i++) {
      this.logInfo(
        i + 1 + ' / ' + sortedSchemas.length + ' -----' + sortedSchemas[i].typeid + '-----'
      );
      await delay(2000);
      await this.uploadSchema(sortedSchemas[i]);
    }
    return this._log;
  };

  /**
   * Orders schemas based on dependicies then uploads schema to forge schema server
   * @param {Array.<object>} schemas Json data of a valid hdfm schema.
   * @return {Promise} Returns a that when resolved will display the full results of the validation
   */
  verifyBulkUploadSchema = async (schemas) => {
    this.log('verifying bulk upload starting');
    const sortedSchemas = this.sortSchemas(schemas);
    const missingSchemas = [];
    const emptySchemas = [];
    const missingPromises = [];
    let different = 0;
    for (let i = 0; i < sortedSchemas.length; i++) {
      this.logInfo(
        i +
          1 +
          ' / ' +
          sortedSchemas.length +
          ' --------------------' +
          sortedSchemas[i].typeid +
          '---------------------'
      );
      const schemaId = sortedSchemas[i].typeid;
      try {
        const result = await this.getVersion('propertySets:' + schemaId);

        const changes = diff(sortedSchemas[i], result.attributes.schema);
        if (changes) {
          this.log('This is different');
          this.log(changes);
          different++;
        }
        if (!Object.keys(result.attributes.schema).length) {
          this.log(result.attributes.schema);
          emptySchemas.push(schemaId);
        } else if (result.attributes.schema.typeid !== schemaId) {
          this.log(result.attributes.schema.typeid + ' : ' + schemaId);
          missingSchemas.push(schemaId);
        }
      } catch (err) {
        this.log('err: ' + schemaId);
        this.log(err.error);
        missingSchemas.push(schemaId);
      }
    }
    this.log('Number of different schemas:' + different);
    this.log(different);
    return Promise.all(missingPromises).then(() => {
      this.log('Missing Schema: ' + missingSchemas.length);
      for (let j = 0; j < missingSchemas.length; j++) {
        this.log(missingSchemas[j]);
      }
      this.log('Empty Schema: ' + emptySchemas.length);
      for (let j = 0; j < emptySchemas.length; j++) {
        this.log(emptySchemas[j]);
      }
    });
  };

  /**
   * Gets information about a namespace from the forge api
   * @param {string} namespaceId The id of the namespace you wish to know more about,
   * @return {*} Returns a promise containing data on the namespace you are looking for
   */
  getNamespace = (namespaceId) => {
    const headers = {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      'x-ads-fcs': true,
    };
    // https://developer.api.autodesk.com/schema/v1/namespaces/:namespace
    return this._request(this._oathToken, this._host, '/namespaces/' + namespaceId, {
      headers,
    });
  };

  /**
   * Gets information about an extension from the forge api
   * @param {string} extensionId The id of the schema you wish to know more about,
   * should look like (entityId:extensionName)
   * @return {*} Returns a promise containing data on the extension you are looking for
   */
  getExtension = (extensionId) => {
    const headers = {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      'x-ads-fcs': true,
    };
    return this._request(this._oathToken, this._host, '/extensions/' + extensionId, {
      method: 'GET',
      headers,
    }).then((res) => {
      return res.data;
    });
  };

  /**
   * Gets information about an extension from the forge api
   * @param {string} extensionId The id of the schema you wish to know more about,
   * should look like (entityId:extensionName)
   * @return {*} Returns a promise containing data on the scehams you are looking for
   */
  getTip = (extensionId) => {
    const headers = {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      'x-ads-fcs': true,
    };
    return this._request(this._oathToken, this._host, '/versions/' + extensionId + '-tip', {
      method: 'GET',
      headers,
    }).then((res) => {
      return res.data;
    });
  };

  /**
   * Gets information about an extension from the forge api
   * @param {string} extensionId The id of the schema you wish to know more about,
   * should look like (entityId:extensionName)
   * @return {*} Returns a promise containing data on the scehams you are looking for
   */
  getDraft = (extensionId) => {
    const headers = {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      'x-ads-fcs': true,
    };
    return this._request(this._oathToken, this._host, '/versions/' + extensionId + '-draft', {
      method: 'GET',
      headers,
    }).then((res) => {
      return res.data;
    });
  };

  /**
   * Gets information about an extension from the forge api
   * @param {string} schemaId The id of the schema you wish to know more about,
   * should look like (entityId:extensionName)
   * @return {*} Returns a promise containing data on the scehams you are looking for
   */
  getVersion = (schemaId) => {
    const headers = {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      'x-ads-fcs': true,
      'Cache-Control': 'no-cache',
    };
    return this._request(this._oathToken, this._host, '/versions/' + schemaId, {
      headers,
      resolveWithFullResponse: true,
      time: true,
    }).then((res) => {
      return JSON.parse(res.body).data;
    });
  };

  /**
   * Creates an extensions using forge api
   * @param {string} entityId The id of the entity you wish to create the schema under
   * @param {string} extensionName The name of the schema you would want to create
   * @return {*} Returns a promise containing the results of the creation
   */
  createExtension = (entityId, extensionName) => {
    const headers = {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      'x-ads-fcs': true,
    };
    const body = {
      data: { type: 'extensions', attributes: { name: extensionName, entityId } },
    };
    const payload = {
      url: this._host + '/entities/' + entityId + '/extensions',
      auth: {
        bearer: this._oathToken,
      },
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    };
    return this._request(
      this._oathToken,
      this._host,
      '/entities/' + entityId + '/extensions',
      payload
    );
  };

  /**
   * Gets all Schemas belonging to a namespce
   * @param {string} namespaceId The id of the namspace
   * @return {*} Returns a promise containing the array of the schemas
   */
  getSchemas = (namespaceId) => {
    const headers = {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      'x-ads-fcs': true,
    };
    return this._request(
      this._oathToken,
      this._host,
      '/schemas?filters[schema][namespace]=' + namespaceId,
      {
        url: this._host + '/schemas?filters[schema][namespace]=' + namespaceId,
        auth: {
          bearer: this._oathToken,
        },
        headers,
      }
    ).then((res) => {
      return res.data;
    });
  };

  /**
   * Sorts an array of schemas based on dependicies.
   * @param {*} inputSchemas The an array of schemas
   * @return {*} Returns the sorted list of schemas
   */
  sortSchemas = (inputSchemas) => {
    // sort by versions at first to make sure we publish schema in the order of versions.
    const schemas = inputSchemas.slice(0);
    schemas.sort((a, b) => {
      const aid = a.typeid.split('-')[0];
      const av = a.typeid.split('-')[1];
      const bid = b.typeid.split('-')[0];
      const bv = b.typeid.split('-')[1];
      if (aid === bid) {
        // disable-eslint no-nested-ternary
        return av < bv ? -1 : av > bv ? 1 : 0;
      } else {
        return aid < bid ? -1 : 1;
      }
    });

    let swapHappened = true;
    while (swapHappened) {
      swapHappened = false;
      for (let i = 0; i < schemas.length; i++) {
        const child = schemas[i];
        const dependicies = this.getDependacies(child);
        if (dependicies.length > 0) {
          for (let j = schemas.length; j > i; --j) {
            if (schemas[j] === undefined || schemas[j] === null) {
              continue;
            }
            const ifDependant = dependicies.indexOf(schemas[j].typeid) !== -1;

            if (ifDependant) {
              schemas[i] = schemas[j];
              schemas[j] = child;
              swapHappened = true;
              break;
            }
          }
        }
      }
    }
    return schemas;
  };

  /**
   * Gets the dependencies of a schema
   * @param {Object} inputSchema A schema
   * @return {*} dependencies of a schema
   */
  getDependacies = (inputSchema) => {
    const child = JSON.parse(JSON.stringify(inputSchema));
    let dependicies = [];

    Object.freeze(child);
    const hasInherits = child.hasOwnProperty('inherits') && _.isArray(child['inherits']);
    const hasConstants = child.hasOwnProperty('constants') && _.isArray(child['constants']);

    if (hasInherits) {
      if (_.isString(child['inherits'])) {
        dependicies = [child['inherits']];
      } else {
        dependicies = child['inherits'].slice();
      }
    }

    if (hasConstants) {
      for (let k = 0; k < child['constants'].length; k++) {
        const constant = child['constants'][k];
        if (constant['typeid']) {
          dependicies.push(constant['typeid']);
        }
        const hasValue = constant.hasOwnProperty('value') && _.isObject(constant['value']);
        if (hasValue) {
          Object.keys(constant['value']).forEach(function (key) {
            dependicies.push(key);
          });
        }

        const hasTypedValue =
          constant.hasOwnProperty('typedValue') && !_.isUndefined(constant['typedValue']);
        if (hasTypedValue) {
          const constantValue = constant['typedValue'];
          if (_.isObject(constantValue) && !_.isArray(constantValue)) {
            if (constantValue['typeid']) {
              dependicies.push(constantValue['typeid']);
            }
          } else {
            for (let z = 0; z < constantValue.length; z++) {
              if (constantValue[z]['typeid']) {
                dependicies.push(constantValue[z]['typeid']);
              }
            }
          }
        }
      }
    }
    return dependicies;
  };

  /**
   * Grant admin, author, reader permissions to people on the namespace.
   * @param {*} namespaceId The namespace we plan to grant the permissions to.
   * @param {*} oxygenIds The user oxygen ids we plan to grant the permissions.
   */
  grantPermissions = (namespaceId, oxygenIds) => {
    const o2Users = Array.isArray(oxygenIds) ? oxygenIds : [oxygenIds];
    const headers = {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      'x-ads-fcs': true,
    };
    const body = {
      data: {
        type: 'namespaces',
        attributes: {
          permissions: [
            {
              role: 'NAMESPACE_ADMIN',
              subjects: {
                o2Users,
              },
            },
            {
              role: 'SCHEMA_AUTHOR',
              subjects: {
                o2Users,
              },
            },
            {
              role: 'SCHEMA_READER',
              subjects: {
                o2Users,
              },
            },
          ],
        },
      },
    };
    const payload = {
      url: this._host + '/namespaces/' + namespaceId,
      auth: {
        bearer: this._oathToken,
      },
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    };
    return this._request(this._oathToken, this._host, '/namespaces/' + namespaceId, payload);
  };

  /**
   * Creates an extensions using forge api
   * @param {string} parentNamespace name of the existing parent namespace you wish your new namespace to be under
   * @param {string} childNamespace name of the namespace you wish to create
   * @return {*} Returns a promise containing the results of the creation
   */
  createNamespace = (parentNamespace, childNamespace) => {
    const headers = {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      'x-ads-fcs': true,
    };
    const isParentACompany = parentNamespace.split('.').length === 1;
    const body = isParentACompany
      ? {
          data: {
            relationships: {
              companies: {
                data: [
                  {
                    type: 'companies',
                    id: parentNamespace,
                  },
                ],
              },
            },
            type: 'namespaces',
            attributes: {
              name: childNamespace,
            },
          },
        }
      : {
          data: {
            relationships: {
              namespaces: {
                data: [
                  {
                    type: 'namespaces',
                    id: parentNamespace,
                  },
                ],
              },
            },
            type: 'namespaces',
            attributes: {
              name: childNamespace,
            },
          },
        };
    const payload = {
      url: this._host + '/namespaces/',
      auth: {
        bearer: this._oathToken,
      },
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    };
    return this._request(this._oathToken, this._host, '/namespaces/', payload);
  };

  isNamespaceExist = (namespaceId) => {
    return !!cachedNamespace[namespaceId];
  };

  MarkNamespaceExist = (namespaceId) => {
    cachedNamespace[namespaceId] = true;
  };

  // autodesk.revit.frabrikam.revit.YZVYJQWWAJ89.frabrikam.unit.unit
  /**
   * Creates namespaces using forge api
   * @param {string} namespace name of the existing parent namespace you wish your new namespace to be under
   * @return {*} Returns a promise containing the results of the creation
   */
  createNamespaces = async (namespaceId) => {
    const names = namespaceId.trim().split('.');
    let namespace = '';
    for (let i = 0; i < names.length; i++) {
      namespace += i === 0 ? names[i] : '.' + names[i];
      if (this.isNamespaceExist(namespace)) {
        continue;
      } else {
        try {
          let response;
          try {
            response = await this.getNamespace(namespace);
          } catch (err) {
            if (err.code === '1038') {
              // console.log(`The namespace ${namespace} doesn't exist.`);
            }
          }
          if (response === undefined) {
            // not exist
            const parentNamespace = namespace.substring(0, namespace.lastIndexOf('.'));
            const newNamespaceName = namespace.substring(namespace.lastIndexOf('.') + 1);
            response = await this.createNamespace(parentNamespace, newNamespaceName);
            if (response.error === undefined) {
              this.MarkNamespaceExist(namespace);
            } else {
              throw new Error('Failed to crate name space because ' + response.error);
            }
          } else {
            // already exist
            this.MarkNamespaceExist(namespace);
          }
        } catch (err) {
          throw err;
        }
      }
    }
  };

  getAllAttchments = (originId) => {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-ads-fcs': true,
    };
    return this._request(
      this._oathToken,
      this._host,
      `/versions/propertySets:${originId}/attachments`,
      {
        url: this._host + `/versions/propertySets:${originId}/attachments`,
        auth: {
          bearer: this._oathToken,
        },
        headers,
      }
    ).then((res) => {
      return res.data;
    });
  };

  getAttachment = (originId, attachmentId) => {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-ads-fcs': true,
    };
    return this._request(
      this._oathToken,
      this._host,
      `/versions/propertySets:${originId}/attachments/${attachmentId}`,
      {
        url: this._host + `/versions/propertySets:${originId}/attachments/${attachmentId}`,
        auth: {
          bearer: this._oathToken,
        },
        headers,
      }
    ).then((res) => {
      return res.data;
    });
  };

  createTagCollection = (companyId) => {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-ads-fcs': true,
    };
    const name = companyId.split('.').pop();
    const body = {
      name,
      externalId: companyId,
    };
    return this._request(this._oathToken, this._host, '/tag-collections', {
      auth: {
        bearer: this._oathToken,
      },
      headers,
      method: 'POST',
      body: JSON.stringify(body),
    }).then((res) => {
      return res.data;
    });
  };

  createTagCategory = (companyId, tagCategoryData) => {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-ads-fcs': true,
    };
    const body = [
      {
        name: tagCategoryData.displayName,
        userData: tagCategoryData,
      },
    ];
    return this._request(this._oathToken, this._host, `/tag-collections/${companyId}/categories`, {
      auth: {
        bearer: this._oathToken,
      },
      headers,
      method: 'POST',
      body: JSON.stringify(body),
    }).then((res) => {
      return res.data;
    });
  };
}

export default ForgeSchemaAPIHelper;
